import * as t from "io-ts";
import React from "react";

import { ConfiguratorTypes } from "../../constants";
import {
    IOptionCode,
    OptionCode,
    OptionSingleValue,
} from "../../models/catalogue";
import { Product } from "../../models/catalogue";
import { DiscountDisplayMode } from "../../models/discountDisplay";
import {
    ImageURL,
    ProductCategoryID,
    ProductID,
    ProductIDs,
    SafeHTML,
} from "../../models/nominals";
import { BaseCompareTile } from "../../models/product-compare";
import {
    CharBlock,
    FlairSubBlock,
    ImageChooserBlock,
    ListBlock,
    ProductChooserSubBlock,
    ProductOptionValueChoiceBlock,
    RichTextBlock,
    SpacingSubBlock,
    StreamValue,
    StructBlock,
} from "../../models/streamfield-blocks";
import { codecFromEnum, nullable } from "../../models/utils";
import { ValuePropBarBlock } from "../../models/value-props";
import { RootProductSelectorInputType } from "./constants";

const VariantPrefilter = t.readonly(
    t.type({
        attr: OptionCode,
        value: OptionSingleValue,
    }),
);
export type IVariantPrefilter = t.TypeOf<typeof VariantPrefilter>;

const RootProductCompareModalContent = t.readonly(
    t.type({
        products: t.readonlyArray(BaseCompareTile),
    }),
);

const CardContentValue = t.readonly(
    t.type({
        badge: nullable(
            t.readonly(
                t.intersection([
                    t.type({
                        display_label: t.string,
                        description: nullable(t.string),
                    }),
                    // Optional so card content cached before the field
                    // existed still decodes.
                    t.partial({
                        style: t.string,
                    }),
                ]),
            ),
        ),
        rows: t.readonlyArray(
            t.readonly(
                t.type({
                    label: nullable(t.string),
                    copy: t.string,
                }),
            ),
        ),
    }),
);
export type ICardContentValue = t.TypeOf<typeof CardContentValue>;

const CardContentOption = t.readonly(
    t.type({
        order: t.readonlyArray(OptionSingleValue),
        values: t.readonly(t.record(OptionSingleValue, CardContentValue)),
    }),
);

const CardContentRootProduct = t.readonly(
    t.type({
        rating: nullable(
            t.readonly(
                t.type({
                    rating: t.number,
                    num_reviews: t.number,
                }),
            ),
        ),
        options: t.readonly(t.record(t.string, CardContentOption)),
    }),
);
export type ICardContentRootProduct = t.TypeOf<typeof CardContentRootProduct>;

/**
 * Product card drawer content, keyed by root product ID, then option code,
 * then option value. See: server.cms.card_content.build_card_content (Sealy)
 */
const CardContent = t.readonly(t.record(t.string, CardContentRootProduct));
type ICardContent = t.TypeOf<typeof CardContent>;

/**
 * The Option Set settings the configurator's reducers need for product card
 * drawers. Built once from the Option Set by getCardDrawerSettings, which
 * returns null when availability filtering is off.
 */
export type ICardDrawerSettings = Readonly<{
    honourAuthoredOrder: boolean;
    cardContent: ICardContent;
    discountDisplayMode: DiscountDisplayMode;
    percentageDiscountFormat: string;
    absoluteDiscountFormat: string;
}>;

/**
 * See model: tsicommon.cms.modular_configurator.ModularConfiguratorOptionSet
 */
export const ModularConfiguratorOptionSet = t.readonly(
    t.intersection([
        t.type({
            product_ids: ProductIDs,
            initial_variant_id: nullable(ProductID),
            variant_prefilter: nullable(VariantPrefilter),
            root_product_selector: t.readonly(
                t.type({
                    input_type: codecFromEnum(
                        "RootProductSelectorInputType",
                        RootProductSelectorInputType,
                    ),
                    label: t.string,
                    enable_ratings: t.boolean,
                }),
            ),
            layout: t.readonly(
                t.intersection([
                    t.type({
                        design: codecFromEnum(
                            "ConfiguratorTypes",
                            ConfiguratorTypes,
                        ),
                        enable_star_rating: t.boolean,
                        enable_quantity_selector: t.boolean,
                        enable_prequal: t.boolean,
                        enable_chat: t.boolean,
                        enable_predicted_date: t.boolean,
                        shipping_callout_icon: nullable(ImageChooserBlock),
                        shipping_callout_content: nullable(t.string),
                        dynamic_shipping_time_messaging: nullable(
                            t.readonly(
                                t.type({
                                    title: t.string,
                                    monday_time: t.string,
                                    monday_before_message: t.string,
                                    monday_after_message: t.string,
                                    tuesday_time: t.string,
                                    tuesday_before_message: t.string,
                                    tuesday_after_message: t.string,
                                    wednesday_time: t.string,
                                    wednesday_before_message: t.string,
                                    wednesday_after_message: t.string,
                                    thursday_time: t.string,
                                    thursday_before_message: t.string,
                                    thursday_after_message: t.string,
                                    friday_time: t.string,
                                    friday_before_message: t.string,
                                    friday_after_message: t.string,
                                    saturday_time: t.string,
                                    saturday_before_message: t.string,
                                    saturday_after_message: t.string,
                                    sunday_time: t.string,
                                    sunday_before_message: t.string,
                                    sunday_after_message: t.string,
                                }),
                            ),
                        ),
                        shipping_message: nullable(
                            t.readonly(
                                t.type({
                                    header: nullable(t.string),
                                    footer: nullable(t.string),
                                    messages: nullable(
                                        t.readonlyArray(t.string),
                                    ),
                                }),
                            ),
                        ),
                        review_aggregation: nullable(
                            t.readonly(
                                t.type({
                                    review_aggregation_copy: t.string,
                                    checkmark_icon: nullable(ImageChooserBlock),
                                    modal_content: nullable(t.string),
                                    modal_image: nullable(ImageChooserBlock),
                                }),
                            ),
                        ),
                    }),
                    // Optional so fragments cached before these flags existed
                    // still decode; absent reads as off.
                    t.partial({
                        only_offer_available_configurations: t.boolean,
                        honour_authored_option_order: t.boolean,
                        two_column_option_drawers: t.boolean,
                    }),
                ]),
            ),
            discounts: t.readonly(
                t.type({
                    discount_mode: codecFromEnum(
                        "DiscountDisplayMode",
                        DiscountDisplayMode,
                    ),
                    percentage_discount_format: t.string,
                    absolute_discount_format: t.string,
                }),
            ),
            root_product_compare_modal_title: nullable(t.string),
            root_product_compare_modal: nullable(
                RootProductCompareModalContent,
            ),
        }),
        t.partial({
            card_content: CardContent,
        }),
    ]),
);
export type IModularConfiguratorOptionSet = t.TypeOf<
    typeof ModularConfiguratorOptionSet
>;

export type IModularConfiguratorShippingMessage = Readonly<{
    header: string | null;
    footer: string | null;
    messages: readonly string[] | null;
}> | null;

/**
 * See block: tsicommon.cms.blocks.modular_configurator._UpsellInfoModalImageSlide
 */
const UpsellInfoModalImageSlide = StructBlock(
    {},
    {
        image: nullable(ImageChooserBlock),
    },
);
export type IUpsellInfoModalImageSlide = t.TypeOf<
    typeof UpsellInfoModalImageSlide
>;

/**
 * See block: tsicommon.cms.blocks.modular_configurator._UpsellInfoModalImageGallery
 */
const UpsellInfoModalImageGallery = StructBlock(
    {
        slides: ListBlock(UpsellInfoModalImageSlide),
    },
    {},
);
export type IUpsellInfoModalImageGallery = t.TypeOf<
    typeof UpsellInfoModalImageGallery
>;

/**
 * See block: tsicommon.cms.blocks.modular_configurator._UpsellInfoModalFeature
 */
const UpsellInfoModalFeature = StructBlock(
    {
        content: CharBlock,
    },
    {},
);

/**
 * See block: tsicommon.cms.blocks.modular_configurator._UpsellInfoModalFeaturesBlock
 */
const UpsellInfoModalFeaturesBlock = StructBlock(
    {
        heading: CharBlock,
        list_icon: nullable(ImageChooserBlock),
        features: ListBlock(UpsellInfoModalFeature),
    },
    {},
);
export type IUpsellInfoModalFeaturesBlock = t.TypeOf<
    typeof UpsellInfoModalFeaturesBlock
>;

/**
 * See block: tsicommon.cms.blocks.modular_configurator._UpsellInfoModalSegmentedRichTextBlock
 */
const UpsellInfoModalSegmentedRichTextBlock = StructBlock(
    {
        segments: ListBlock(RichTextBlock),
    },
    {},
);
export type IUpsellInfoModalSegmentedRichTextBlock = t.TypeOf<
    typeof UpsellInfoModalSegmentedRichTextBlock
>;

/**
 * See block: tsicommon.cms.blocks.modular_configurator._UpsellInfoModalRichTextBlock
 */
const UpsellInfoModalRichTextBlock = StructBlock(
    {
        heading: nullable(CharBlock),
        content: nullable(RichTextBlock),
    },
    {},
);
export type IUpsellInfoModalRichTextBlock = t.TypeOf<
    typeof UpsellInfoModalRichTextBlock
>;

/**
 * See block: tsicommon.cms.blocks.modular_configurator.ModularConfigurator_UpsellInfoModalContent
 */
const ModularConfigurator_UpsellInfoModalContent = t.union([
    StreamValue("features", UpsellInfoModalFeaturesBlock),
    StreamValue("image_gallery", UpsellInfoModalImageGallery),
    StreamValue("rich_text", UpsellInfoModalRichTextBlock),
    StreamValue("segmented_rich_text", UpsellInfoModalSegmentedRichTextBlock),
    StreamValue("value_props", ValuePropBarBlock),
]);
export type IModularConfigurator_UpsellInfoModalContent = t.TypeOf<
    typeof ModularConfigurator_UpsellInfoModalContent
>;

/**
 * See block: tsicommon.cms.modular_configurator.ModularConfigurator_UpsellInfoModal
 */
const ModularConfigurator_UpsellInfoModalProduct = t.readonly(
    t.type({
        product: nullable(Product),
        flair: nullable(ListBlock(StreamValue("flair", FlairSubBlock))),
        content: t.readonlyArray(ModularConfigurator_UpsellInfoModalContent),
    }),
);
export type IModularConfigurator_UpsellInfoModalProduct = t.TypeOf<
    typeof ModularConfigurator_UpsellInfoModalProduct
>;

/**
 * See block: tsicommon.cms.modular_configurator.ModularConfigurator_UpsellInfoModal
 */
const ModularConfiguratorUpsellInfoModal = t.readonly(
    t.type({
        id: t.number,
        bundle: t.number,
        bundle_type: t.string,
        bundle_headline: t.string,
        bundle_description: t.string,
        card_flair: nullable(ListBlock(StreamValue("flair", FlairSubBlock))),
        upsell_info_products: t.readonlyArray(
            ModularConfigurator_UpsellInfoModalProduct,
        ),
    }),
);
export type IModularConfiguratorUpsellInfoModal = t.TypeOf<
    typeof ModularConfiguratorUpsellInfoModal
>;

export const ModularConfiguratorUpsellInfoModals = t.readonlyArray(
    ModularConfiguratorUpsellInfoModal,
);

/**
 * See streamfield block: tsicommon.cms.blocks.product_line_collection.InfoPanel
 */
const PLCInfoPanel = t.readonly(
    t.type({
        body: SafeHTML,
    }),
);
export type IPLCInfoPanel = t.TypeOf<typeof PLCInfoPanel>;

/**
 * See streamfield block: tsicommon.cms.blocks.product_line_collection.CategorySelectorOption
 */
const PLCProductCategorySelectorOption = t.readonly(
    t.type({
        category: t.readonly(
            t.type({
                id: ProductCategoryID,
                name: t.string,
            }),
        ),
        image: nullable(ImageChooserBlock),
    }),
);
export type IPLCProductCategorySelectorOption = t.TypeOf<
    typeof PLCProductCategorySelectorOption
>;

/**
 * See streamfield block: tsicommon.cms.blocks.product_line_collection.CategorySelector
 */
const PLCProductCategorySelector = StreamValue(
    "selector",
    t.readonly(
        t.type({
            name: t.string,
            helptext: SafeHTML,
            learn_more: PLCInfoPanel,
            options: t.readonlyArray(PLCProductCategorySelectorOption),
        }),
    ),
);
export type IPLCProductCategorySelector = t.TypeOf<
    typeof PLCProductCategorySelector
>;
export const PLCProductCategorySelectors = t.readonlyArray(
    PLCProductCategorySelector,
);

/**
 * See streamfield block: tsicommon.cms.blocks.product_line_collection.BooleanOptionSelectorOption
 */
export const PLCBooleanOptionSelectorOption = t.readonly(
    t.type({
        name: t.string,
        attribute: OptionCode,
        show_in_match_model: t.boolean,
        description: t.string,
        image: nullable(ImageChooserBlock),
        help_content: PLCInfoPanel,
    }),
);
export type IPLCBooleanOptionSelectorOption = t.TypeOf<
    typeof PLCBooleanOptionSelectorOption
>;

/**
 * See streamfield block: tsicommon.cms.blocks.product_line_collection.OtherOptionSelectorOption
 */
const PLCOtherOptionSelectorOption = t.readonly(
    t.type({
        name: t.string,
        value: t.string,
        description: t.string,
        image: nullable(ImageChooserBlock),
        help_content: PLCInfoPanel,
    }),
);

/**
 * See streamfield block: tsicommon.cms.blocks.product_line_collection.MattressSizeOptionSelector
 */
export const PLCMattressSizeOptionSelector = StreamValue(
    "mattress_size",
    t.readonly(
        t.type({
            attribute: OptionCode,
            show_in_match_model: t.boolean,
            help_content: PLCInfoPanel,
        }),
    ),
);
export type IPLCMattressSizeOptionSelector = t.TypeOf<
    typeof PLCMattressSizeOptionSelector
>;

/**
 * See streamfield block: tsicommon.cms.blocks.product_line_collection.MattressFeelOptionSelector
 */
export const PLCMattressFeelOptionSelector = StreamValue(
    "mattress_feel",
    t.readonly(
        t.type({
            attribute: OptionCode,
            show_in_match_model: t.boolean,
            help_content: PLCInfoPanel,
        }),
    ),
);
export type IPLCMattressFeelOptionSelector = t.TypeOf<
    typeof PLCMattressFeelOptionSelector
>;

/**
 * See streamfield block: tsicommon.cms.blocks.product_line_collection.BooleanOptionSelectorGroup
 */
export const PLCBooleanOptionSelectorGroup = StreamValue(
    "boolean_option_group",
    t.readonly(
        t.type({
            selectors: t.readonlyArray(PLCBooleanOptionSelectorOption),
        }),
    ),
);
export type IPLCBooleanOptionSelectorGroup = t.TypeOf<
    typeof PLCBooleanOptionSelectorGroup
>;

/**
 * See streamfield block: tsicommon.cms.blocks.product_line_collection.OtherOptionSelector
 */
export const PLCOtherOptionSelector = StreamValue(
    "other",
    t.readonly(
        t.type({
            name: t.string,
            attribute: OptionCode,
            show_in_match_model: t.boolean,
            options: t.readonlyArray(PLCOtherOptionSelectorOption),
        }),
    ),
);
export type IPLCOtherOptionSelector = t.TypeOf<typeof PLCOtherOptionSelector>;

/**
 * See streamfield block: tsicommon.cms.blocks.product_line_collection.ProductOptionPanelSelectors
 */
const PLCProductOptionSelector = t.union([
    PLCMattressSizeOptionSelector,
    PLCMattressFeelOptionSelector,
    PLCBooleanOptionSelectorGroup,
    PLCOtherOptionSelector,
]);
export type IPLCProductOptionSelector = t.TypeOf<
    typeof PLCProductOptionSelector
>;

/**
 * See streamfield block: tsicommon.cms.blocks.product_line_collection.ProductOptionPanel
 */
const PLCOptionPanel = StreamValue(
    "panel",
    t.readonly(
        t.type({
            short_name: t.string,
            long_name: t.string,
            selectors: t.readonlyArray(PLCProductOptionSelector),
        }),
    ),
);
export type IPLCOptionPanel = t.TypeOf<typeof PLCOptionPanel>;
export const PLCOptionPanels = t.readonlyArray(PLCOptionPanel);

/**
 * See streamfield block: tsicommon.cms.blocks.tab_feature_attribute_block._FeatureAttributeModel
 */
export type ITabFeatureAttributeBlock_ModelTab = Readonly<{
    model: t.TypeOf<typeof ProductChooserSubBlock>;
    model_name: string;
    image: t.TypeOf<typeof ImageChooserBlock>;
    content: t.TypeOf<typeof RichTextBlock>;
    image_mobile?: t.TypeOf<typeof ImageChooserBlock>;
    flair?: t.TypeOf<typeof FlairSubBlock>;
}>;

/**
 * See streamfield block: tsicommon.cms.blocks.tab_feature_attribute_block._FeatureAttribute
 */
export type ITabFeatureAttributeBlock_AttributeTab = Readonly<{
    attribute_value: t.TypeOf<typeof ProductOptionValueChoiceBlock>;
    display_name?: string;
    icon: t.TypeOf<typeof ImageChooserBlock>;
    tabs: readonly ITabFeatureAttributeBlock_ModelTab[];
}>;

/**
 * See streamfield block: tsicommon.cms.blocks.tab_feature_attribute_block.TabFeatureAttributeBlock
 */
export type ITabFeatureAttributeBlock = Readonly<{
    spacing: t.TypeOf<typeof SpacingSubBlock>;
    header_content: t.TypeOf<typeof RichTextBlock>;
    attribute: IOptionCode;
    tabs: readonly ITabFeatureAttributeBlock_AttributeTab[];
}>;

const ModularConfiguratorPromoCardInfoModal = t.readonly(
    t.type({
        flair: nullable(ListBlock(StreamValue("flair", FlairSubBlock))),
        image: nullable(ImageURL),
        title: nullable(t.string),
        content: t.readonlyArray(ModularConfigurator_UpsellInfoModalContent),
    }),
);
export type IModularConfiguratorPromoCardInfoModal = t.TypeOf<
    typeof ModularConfiguratorPromoCardInfoModal
>;

const ModularConfiguratorPromoCard = t.readonly(
    t.type({
        id: t.number,
        section_title: nullable(t.string),
        product: nullable(ProductID),
        card_title: nullable(t.string),
        product_name: nullable(t.string),
        description: nullable(t.string),
        card_image_url: nullable(ImageURL),
        flair: nullable(ListBlock(StreamValue("flair", FlairSubBlock))),
        info_modal: nullable(ModularConfiguratorPromoCardInfoModal),
    }),
);
export type IModularConfiguratorPromoCard = t.TypeOf<
    typeof ModularConfiguratorPromoCard
>;

export const ModularConfiguratorPromoCards = t.readonlyArray(
    ModularConfiguratorPromoCard,
);

export type IUpsellModalComponentRef = Readonly<{
    openModal: (event?: React.MouseEvent<HTMLElement>) => void;
}>;

export type IUpsellModalProps = Readonly<{
    ref: React.RefObject<IUpsellModalComponentRef | null>;
    bundles: readonly import("../../models/catalogue").IConcreteBundle[];
    onProceed: () => void;
}>;

export type IUpsellModalComponentFn = (
    props: IUpsellModalProps,
) => React.ReactNode;
